/**
 * Resolves a CRS display name from a CRS list and a property's crsId.
 *
 * The property's `crsId` corresponds to the `id` field of an entry returned
 * by `getCRS()`. Prefers `displayName`; falls back to `name` when displayName
 * is null/empty (e.g., the "Trader" entry has no displayName).
 *
 * Returns "None" when crsId is missing, the list is unavailable, or no match
 * is found.
 */
export const getCrsDisplayName = (crsList, crsId) => {
  if (!crsId || !Array.isArray(crsList)) return "None";
  const match = crsList.find((entry) => entry.id === crsId);
  if (!match) return "None";
  return match.displayName || match.name || "None";
};

/**
 * Partitions `childAgeRanges` into the infants range and the child range.
 *
 * The infants entry is identified by a case-insensitive match on
 * `rangeName === "infants"`. The child entry is the first non-infants entry
 * in the array (the UI displays a single min/max child age pair, so only the
 * first child-type range is surfaced).
 *
 * Returns `{ infants, child }` where each may be `null` if not present.
 * Safely handles null/undefined/non-array inputs.
 */
export const getChildAgeRanges = (childAgeRanges) => {
  if (!Array.isArray(childAgeRanges)) return { infants: null, child: null };
  const isInfants = (entry) =>
    typeof entry?.rangeName === "string" &&
    entry.rangeName.toLowerCase() === "infants";
  const infants = childAgeRanges.find(isInfants) || null;
  const child =
    childAgeRanges.find((entry) => entry && !isInfants(entry)) || null;
  return { infants, child };
};
